import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { formatScanDate as defaultFormatScanDate } from '../formatScanDate';
import type { DmScanSummary } from '../types';
import { TimelineHeader } from './TimelineHeader';
import { TimelineMinimap } from './TimelineMinimap';
import { TimelineTrack } from './TimelineTrack';
import { buildMonthBands, findIntervals, orderScans, placeScans, spreadScans } from './scanScale';
import { useContainerWidth } from './useContainerWidth';
import { useViewport } from './useViewport';
import { useWheelZoom } from './useWheelZoom';

export interface ScanTimelineProps {
    scans: DmScanSummary[] | null | undefined;
    scansLoading?: boolean;
    selectedScanId: number | null;
    selectedScan: DmScanSummary | null;
    onSelectScan: (scanId: number) => void;
    totalAligners?: number | null;
    monitoringStatus?: string | null;
    currentAligner?: number | null;
    formatScanDate?: (value: string) => string;
    /** Hide the track and keep the header only — for surfaces pinned to one scan. */
    showTrack?: boolean;
    hasSidePanel?: boolean;
    showSidePanel?: boolean;
    onToggleSidePanel?: () => void;
    /** False on a case DM issues no aligner instructions for; see `DmScanList`. */
    tracksAligners?: boolean;
    /** Host controls for the header, before the Details toggle. */
    headerActions?: ReactNode;
    /** Rendered right after the Details toggle. */
    detailsMenu?: ReactNode;
}

function ScanTimeline({
    scans,
    scansLoading = false,
    selectedScanId,
    selectedScan,
    onSelectScan,
    totalAligners = null,
    monitoringStatus,
    currentAligner,
    formatScanDate = defaultFormatScanDate,
    showTrack = true,
    hasSidePanel = false,
    showSidePanel = true,
    onToggleSidePanel,
    tracksAligners = true,
    headerActions,
    detailsMenu
}: ScanTimelineProps) {
    const [trackRef, width] = useContainerWidth<HTMLDivElement>();
    // Pinned at mount so the layout memo stays pure across re-renders.
    const [now] = useState(() => Date.now());
    const optionPrefix = useId();

    // Undated scans can't be placed, so keeping them would put positions and keyboard
    // steps out of step with the markers actually drawn.
    const entries = useMemo(
        () => orderScans(scans ?? []).filter((entry) => entry.time != null),
        [scans]
    );
    const selectedIndex = entries.findIndex((entry) => entry.scan.scanId === selectedScanId);
    const selectedTime = selectedIndex === -1 ? null : entries[selectedIndex].time;
    const view = useViewport(entries, now, selectedTime);
    const { full, viewport } = view;

    const model = useMemo(() => {
        // Before the first measurement every x collapses to 0, which would paint the
        // whole run piled on the left edge for a frame.
        if (width <= 0) {
            return { points: [], ticks: [], selectedX: null, bands: [], intervals: [] };
        }
        const points = spreadScans(placeScans(entries, viewport, width, selectedScanId), width);
        // Connectors read the painted positions, so a nudged marker keeps its line.
        const paintedX = new Map(points.map((point) => [point.scan.scanId, point.x]));
        return {
            points,
            ticks: placeScans(entries, full, width, selectedScanId),
            selectedX: points.find((point) => point.isSelected)?.x ?? null,
            bands: buildMonthBands(viewport, width),
            intervals: findIntervals(entries, viewport, width, paintedX)
        };
    }, [entries, full, selectedScanId, viewport, width]);

    const wheelRef = useWheelZoom<HTMLDivElement>({
        viewport,
        width,
        zoomAt: view.zoomAt,
        pan: view.pan
    });

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (entries.length === 0) return;
        // Claimed before the end checks, so arrowing off either end doesn't scroll the page.
        event.preventDefault();
        const step = event.key === 'ArrowLeft' ? -1 : 1;
        const from = selectedIndex === -1 ? (step === 1 ? -1 : entries.length) : selectedIndex;
        const next = Math.min(entries.length - 1, Math.max(0, from + step));
        if (next === selectedIndex) return;
        onSelectScan(entries[next].scan.scanId);
    };

    if (scansLoading) {
        return <div className="h-4 w-32 shrink-0 animate-pulse rounded bg-gray-200" />;
    }

    return (
        <div className="flex shrink-0 flex-col gap-2">
            {showTrack && entries.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <TimelineTrack
                        trackRef={trackRef}
                        width={width}
                        bands={model.bands}
                        intervals={model.intervals}
                        points={model.points}
                        selectedX={model.selectedX}
                        activeDescendantId={
                            model.selectedX == null
                                ? undefined
                                : `${optionPrefix}-${selectedScanId}`
                        }
                        idPrefix={optionPrefix}
                        onSelect={onSelectScan}
                        wheelRef={wheelRef}
                        onKeyDown={onKeyDown}
                        tracksAligners={tracksAligners}
                    />
                    <TimelineMinimap
                        full={full}
                        viewport={viewport}
                        ticks={model.ticks}
                        width={width}
                        onPan={view.pan}
                        onResize={view.resize}
                        onCentreOn={view.centreOn}
                        onFit={view.fit}
                        tracksAligners={tracksAligners}
                    />
                </div>
            )}

            <TimelineHeader
                scan={selectedScan}
                totalAligners={totalAligners}
                monitoringStatus={monitoringStatus}
                currentAligner={currentAligner}
                formatScanDate={formatScanDate}
                hasSidePanel={hasSidePanel}
                showSidePanel={showSidePanel}
                onToggleSidePanel={onToggleSidePanel}
                tracksAligners={tracksAligners}
                headerActions={headerActions}
                detailsMenu={detailsMenu}
            />
        </div>
    );
}

export { ScanTimeline };
